import React, { useState } from 'react';
import { FiCheck, FiX, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';

const DataPreview = ({ data, validationResults, type, onUpload, onCancel, fileName, onTestSingle }) => {
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const validRecords = validationResults.filter(result => result.isValid);
  const invalidRecords = validationResults.filter(result => !result.isValid);

  const displayData = showOnlyErrors ? invalidRecords : validationResults;
  const totalPages = Math.ceil(displayData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = displayData.slice(startIndex, endIndex);

  const getFieldsToShow = () => {
    if (type === 'Student') {
      return [
        'studentFirstName', 'studentLastName', 'class', 'address', 'city', 'area',
        'parentFirstName', 'parentLastName', 'parentMobile', 'parentEmail',
        // NEW: Pickup From/To and Drop From/To
        // Only Pickup From and Drop To need coordinates (home locations)
        // Pickup To and Drop From are just school names (no coordinates needed)
        'pickupFrom', 'pickupFromLat', 'pickupFromLng',
        'pickupTo',
        'dropFrom',
        'dropTo', 'dropToLat', 'dropToLng'
      ];
    } else {
      return [
        'firstName', 'lastName', 'department', 'address', 'city', 'area', 'mobileNumber'
      ];
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      studentFirstName: 'First Name',
      studentLastName: 'Last Name',
      firstName: 'First Name',
      lastName: 'Last Name',
      class: 'Class',
      department: 'Department',
      address: 'Address',
      city: 'City',
      area: 'Area',
      parentFirstName: 'Parent First Name',
      parentLastName: 'Parent Last Name',
      parentMobile: 'Parent Mobile',
      parentEmail: 'Parent Email',
      mobileNumber: 'Mobile Number',
      // NEW: Pickup From/To and Drop From/To labels
      pickupFrom: 'Pickup From',
      pickupFromLat: 'Pickup Lat',
      pickupFromLng: 'Pickup Lng',
      pickupTo: 'Pickup To (School)',
      dropFrom: 'Drop From (School)',
      dropTo: 'Drop To',
      dropToLat: 'Drop Lat',
      dropToLng: 'Drop Lng'
    };
    return labels[field] || field;
  };

  // Format coordinate values for display
  const formatCoordinate = (value, isAutoFilled) => {
    if (value === undefined || value === null || value === '') {
      return <span style={{ color: '#999' }}>—</span>;
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return <span style={{ color: '#dc3545' }}>{value}</span>;
    }
    // Show with 4 decimal places and highlight if auto-filled
    const formatted = num.toFixed(4);
    if (isAutoFilled) {
      return <span style={{ color: '#28a745', fontWeight: '500' }} title="Auto-filled from location name">✓ {formatted}</span>;
    }
    return formatted;
  };

  // Helper to determine if a field is a coordinate field and get auto-fill status
  const getCoordinateAutoFillStatus = (field, itemData) => {
    // Map coordinate fields to their auto-fill flags
    // Only Pickup From and Drop To need coordinates (home locations)
    const autoFillMap = {
      'pickupFromLat': itemData.pickupFromCoordsAutoFilled,
      'pickupFromLng': itemData.pickupFromCoordsAutoFilled,
      'dropToLat': itemData.dropToCoordsAutoFilled,
      'dropToLng': itemData.dropToCoordsAutoFilled,
    };
    return autoFillMap[field] || false;
  };

  // Check if field is a coordinate field
  const isCoordinateField = (field) => {
    return [
      'pickupFromLat', 'pickupFromLng',
      'dropToLat', 'dropToLng'
    ].includes(field);
  };

  return (
    <div className="data-preview">
      <div className="preview-header">
        <h3>Data Preview - {fileName}</h3>
        <div className="preview-stats">
          <div className="stat valid">
            <FiCheck /> {validRecords.length} Valid Records
          </div>
          <div className="stat invalid">
            <FiX /> {invalidRecords.length} Invalid Records
          </div>
          <div className="stat total">
            Total: {validationResults.length} Records
          </div>
        </div>
      </div>

      <div className="preview-controls">
        <div className="filter-controls">
          <button
            className={`filter-btn ${!showOnlyErrors ? 'active' : ''}`}
            onClick={() => {
              setShowOnlyErrors(false);
              setCurrentPage(1);
            }}
          >
            <FiEye /> Show All Records
          </button>
          <button
            className={`filter-btn ${showOnlyErrors ? 'active' : ''}`}
            onClick={() => {
              setShowOnlyErrors(true);
              setCurrentPage(1);
            }}
          >
            <FiEyeOff /> Show Only Errors
          </button>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="preview-table-container">
        <table className="preview-table">
          <thead>
            <tr>
              <th>Row</th>
              <th>Status</th>
              {getFieldsToShow().map(field => (
                <th key={field}>{getFieldLabel(field)}</th>
              ))}
              <th>Errors</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item, index) => (
              <tr key={startIndex + index} className={item.isValid ? 'valid-row' : 'invalid-row'}>
                <td>{item.rowIndex}</td>
                <td>
                  {item.isValid ? (
                    <FiCheck className="status-icon valid" />
                  ) : (
                    <FiX className="status-icon invalid" />
                  )}
                </td>
                {getFieldsToShow().map(field => (
                  <td key={field} className="data-cell">
                    {isCoordinateField(field) ? (
                      formatCoordinate(
                        item.data[field],
                        getCoordinateAutoFillStatus(field, item.data)
                      )
                    ) : (
                      item.data[field] || '-'
                    )}
                  </td>
                ))}
                <td className="errors-cell">
                  {item.errors.length > 0 && (
                    <div className="error-list">
                      {item.errors.map((error, errorIndex) => (
                        <div key={errorIndex} className="error-item">
                          <FiAlertCircle className="error-icon" />
                          {error}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="preview-actions">
        <button className="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        {onTestSingle && validRecords.length > 0 && (
          <button
            className="test-btn"
            onClick={onTestSingle}
            style={{
              background: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              margin: '0 10px'
            }}
          >
            🧪 Test Single Record
          </button>
        )}
        <button
          className="upload-btn"
          onClick={onUpload}
          disabled={validRecords.length === 0}
        >
          Upload {validRecords.length} Valid Records
        </button>
      </div>
    </div>
  );
};

export default DataPreview;